import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import StatusPill from './StatusPill';
import { Doc, Shield, Tick } from './AppIcons';
import { brandify } from '../../components/Brand';
import { CATEGORY_META } from '../data';
import {
  getEntityChecklist,
  CATEGORY_TO_KEBAB,
  type ChecklistItem as ChecklistRow,
  type EntityChecklistDto,
} from '../../lib/entities/checklist';

/**
 * One entity's compliance checklist, identical in logic for every entity type.
 *
 * The model, shared by company / employee / case: EVERY document (and attestation) type scoped to this
 * kind of entity applies to EVERY entity of that kind. The predicates never decide WHETHER a type is on
 * the list; they only decide its TIER (the category), and therefore whether it counts toward compliance.
 * So the list is the whole scoped set, rendered at its resolved tier, never filtered. That is exactly how
 * the company Documents page behaves; this component keeps employee and case files behaving the same way.
 */
export default function EntityChecklistSection({
  base,
  id,
  title,
  entityNoun,
  possessive,
  reloadKey,
}: {
  /** The entity collection path, e.g. "/api/employees" or "/api/cases". */
  base: string;
  id: string;
  title: string;
  /** Singular noun for the copy, e.g. "employee" or "case". */
  entityNoun: string;
  /** Whose answers set the tiers, e.g. "Priya's" or "this case's". */
  possessive: string;
  /** Bump to force a re-resolve after the entity's predicate answers change. */
  reloadKey?: number;
}) {
  const [checklist, setChecklist] = useState<EntityChecklistDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    getEntityChecklist(base, id)
      .then((c) => alive && setChecklist(c))
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : 'Could not load the requirements.');
      });
    return () => {
      alive = false;
    };
  }, [base, id, reloadKey]);

  // The whole scoped set, at tier. Compliance is measured over the ones that resolved to a needed tier.
  const rows = checklist ? [...checklist.documents, ...checklist.attestations] : [];
  const needed = rows.filter((r) => r.needed);
  const missing = needed.filter((r) => r.status === 'missing');

  return (
    <section className="acard acard--pad">
      <div className="emp-checkshead">
        <h2 className="acard__title">{title}</h2>
        {needed.length > 0 && (
          <StatusPill variant={missing.length === 0 ? 'ok' : 'warn'}>
            {missing.length === 0 ? 'All in place' : `${missing.length} outstanding`}
          </StatusPill>
        )}
      </div>
      <p className="acard__sub">
        {error
          ? error
          : checklist === null
            ? 'Working out the requirements…'
            : `The documents every ${entityNoun} needs, at the tier ${possessive} answers set. ${
                needed.length - missing.length
              } of ${needed.length} required in place.`}
      </p>
      {rows.length === 0 ? (
        <div className="empty">
          {checklist === null && !error ? 'Loading…' : `No ${entityNoun} documents defined yet.`}
        </div>
      ) : (
        <ul className="emp-checks">
          {rows.map((item) => (
            <ChecklistRowItem key={item.id} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One resolved obligation, rendered at whatever tier resolution gave it: present is on file, a
 * needed-but-absent one is outstanding, a not-relevant one is inert, a should-not-have one is flagged.
 * Nothing is hidden; the tier tag and the status together say where each document stands for this entity.
 */
function ChecklistRowItem({ item }: { item: ChecklistRow }) {
  const tone =
    item.status === 'present'
      ? 'done'
      : item.status === 'missing' || item.status === 'needsAnswer'
        ? 'todo'
        : item.status === 'shouldNotHave'
          ? 'danger'
          : 'inert';
  return (
    <li className={`emp-check emp-check--${tone}`}>
      <span className="emp-check__icon">
        {item.documentId !== undefined || item.scope !== 'employerLevel' ? (
          <Doc size={16} />
        ) : (
          <Shield size={16} />
        )}
      </span>
      <div className="emp-check__body">
        <div className="emp-check__name-row">
          <span className="emp-check__name">{item.name}</span>
          {/* No tier tag for needsAnswer: its category is genuinely unknown until answered. */}
          {item.category && (
            <span className="emp-cat">{CATEGORY_META[CATEGORY_TO_KEBAB[item.category]].label}</span>
          )}
        </div>
        {item.status === 'present' ? (
          <span className="emp-status emp-status--done">
            <span className="emp-status__tick">
              <Tick size={9} color="#fff" />
            </span>
            <span>{brandify('On file')}</span>
          </span>
        ) : item.status === 'needsAnswer' ? (
          <span className="emp-status emp-status--todo">
            <span className="emp-status__ring" />
            <span>Answer required to place this</span>
          </span>
        ) : item.status === 'missing' ? (
          <span className="emp-status emp-status--todo">
            <span className="emp-status__ring" />
            <span>Not on file yet</span>
          </span>
        ) : item.status === 'shouldNotHave' ? (
          <span className="emp-status emp-status--danger">
            <span>Should not apply here</span>
          </span>
        ) : (
          <span className="emp-status emp-status--inert">
            <span>Not needed here</span>
          </span>
        )}
      </div>
      {item.status === 'present' && item.documentId && (
        <span className="emp-check__action">
          <Link to={`/app/documents/${item.documentId}`} className="btn btn-green-soft emp-check__btn">
            Open
          </Link>
        </span>
      )}
    </li>
  );
}
